"use client"

import type { UpgradeDefinition, UpgradeNodeStatus, UpgradeColumn } from "@/types/upgrade.types"
import { getUpgradeCost } from "@/systems/upgrade-system"

interface UpgradeTreeNodeProps {
  upgrade: UpgradeDefinition
  status: UpgradeNodeStatus
  canAfford: boolean
  purchaseCount: number
  onSelect: (upgrade: UpgradeDefinition) => void
  isMobile?: boolean
  isJustUnlocked?: boolean
}

// Get unlock effect colors based on column
const getUnlockEffectColors = (column: UpgradeColumn) => {
  switch (column) {
    case "military":
      return {
        line: "linear-gradient(90deg, transparent 0%, #3b82f6 20%, #60a5fa 50%, #3b82f6 80%, transparent 100%)",
        shadow: "#3b82f6",
        flash: "rgba(59, 130, 246, 0.4)",
      }
    case "arcane":
      return {
        line: "linear-gradient(90deg, transparent 0%, #a855f7 20%, #c084fc 50%, #a855f7 80%, transparent 100%)",
        shadow: "#a855f7",
        flash: "rgba(168, 85, 247, 0.4)",
      }
    case "command":
      return {
        line: "linear-gradient(90deg, transparent 0%, #f97316 20%, #fb923c 50%, #f97316 80%, transparent 100%)",
        shadow: "#f97316",
        flash: "rgba(249, 115, 22, 0.4)",
      }
  }
}

// Get badge background colors based on column
const getBadgeColors = (column: UpgradeColumn) => {
  switch (column) {
    case "military":
      return "bg-blue-600 border-blue-400"
    case "arcane":
      return "bg-purple-600 border-purple-400"
    case "command":
      return "bg-orange-600 border-orange-400"
  }
}

export const UpgradeTreeNode = ({
  upgrade,
  status,
  canAfford,
  purchaseCount,
  onSelect,
  isMobile = false,
  isJustUnlocked = false,
}: UpgradeTreeNodeProps) => {
  const isMaxed = status === "maxed"
  const isClickable = (status === "available" || status === "unlocked") && canAfford && !isMaxed
  const isUnlocked = status === "unlocked" || status === "maxed"
  const isBlocked = status === "blocked"
  const isLocked = status === "locked"

  const handleClick = () => {
    if (isMobile || isClickable) {
      onSelect(upgrade)
    }
  }

  // Node background/border colors based on status
  const getNodeClasses = () => {
    const base =
      "relative w-16 h-16 rounded-lg border-2 flex items-center justify-center transition-all duration-200"

    if (isMaxed) {
      // Maxed out - golden glow
      return `${base} bg-amber-900/60 border-amber-500/70 shadow-[0_0_12px_rgba(245,158,11,0.4)]`
    }
    if (isUnlocked && !isMaxed) {
      // Purchased but can buy more
      return `${base} bg-emerald-900/60 border-emerald-500/70 shadow-[0_0_12px_rgba(16,185,129,0.4)] cursor-pointer hover:bg-emerald-800/60 hover:border-emerald-400/70 hover:scale-105`
    }
    if (isBlocked) {
      return `${base} bg-red-950/40 border-red-800/50 opacity-50 cursor-not-allowed`
    }
    if (isClickable) {
      return `${base} bg-amber-900/50 border-amber-500/60 shadow-[0_0_12px_rgba(245,158,11,0.4)] cursor-pointer hover:bg-amber-800/60 hover:border-amber-400/70 hover:scale-105`
    }
    if (status === "available" && !canAfford) {
      return `${base} bg-amber-900/30 border-amber-600/40 opacity-70 cursor-not-allowed`
    }
    // Locked
    return `${base} bg-slate-800/50 border-slate-600/40 opacity-60`
  }

  // Render upgrade icon
  const renderIcon = () => {
    if (isBlocked) {
      return (
        <div className="text-red-400 text-xl font-bold">✕</div>
      )
    }
    // Show icon for all states except blocked (including locked)
    if (upgrade.icon) {
      return (
        <img
          src={upgrade.icon}
          alt={upgrade.name}
          className={`w-full h-full object-cover rounded-md ${isLocked ? "opacity-50 grayscale" : ""}`}
          style={{ imageRendering: "pixelated" }}
        />
      )
    }
    // Fallback placeholder
    return (
      <div className="w-full h-full bg-gradient-to-br from-amber-600/60 to-amber-800/60 rounded-md" />
    )
  }

  // Purchase count badge with column-colored background
  const renderPurchaseBadge = () => {
    if (purchaseCount <= 0) return null

    const badgeColors = getBadgeColors(upgrade.column)

    return (
      <div
        className={`absolute -bottom-0.5 -right-0.5 min-w-4 h-4 ${badgeColors} rounded-md flex items-center justify-center border px-0.5`}
      >
        <span className="text-[10px] text-white font-bold">
          {isMaxed ? "MAX" : purchaseCount}
        </span>
      </div>
    )
  }

  // Active ability indicator
  const renderActiveIndicator = () => {
    if (upgrade.type !== "active") return null
    return (
      <div className="absolute -top-1 -right-1 w-4 h-4 bg-purple-500 rounded-full border border-purple-300 flex items-center justify-center">
        <span className="text-[8px] text-white font-bold">A</span>
      </div>
    )
  }

  // Cost badge - bottom left, shows next level cost
  const renderCostBadge = () => {
    // Hide if maxed, blocked, or already has purchase badge
    if (isMaxed || isBlocked) return null
    // Show cost for available (unpurchased) or unlocked (can upgrade more)
    if (status !== "available" && status !== "unlocked") return null

    const nextCost = getUpgradeCost(upgrade, purchaseCount)

    return (
      <div
        className={`absolute -bottom-0.5 -left-0.5 flex items-center gap-0.5 text-[10px] font-bold ${
          canAfford ? "text-game-gold" : "text-slate-500"
        }`}
      >
        <img
          src="/images/icon-coins.png"
          alt=""
          className="w-3 h-3"
          style={{ imageRendering: "pixelated" }}
        />
        {nextCost}
      </div>
    )
  }

  // Render unlock effect
  const renderUnlockEffect = () => {
    if (!isJustUnlocked) return null
    const colors = getUnlockEffectColors(upgrade.column)
    return (
      <>
        {/* Sweeping horizontal line */}
        <div
          className="absolute left-0 right-0 h-1 pointer-events-none animate-[cooldown-sweep_0.5s_ease-out_forwards] rounded-lg"
          style={{
            background: colors.line,
            boxShadow: `0 0 8px ${colors.shadow}, 0 0 16px ${colors.shadow}`,
            bottom: 0,
          }}
        />
        {/* Flash overlay */}
        <div
          className="absolute inset-0 pointer-events-none animate-[cooldown-flash_0.3s_ease-out_forwards] rounded-lg"
          style={{ backgroundColor: colors.flash }}
        />
      </>
    )
  }

  return (
    <div className="relative group">
      <div className={getNodeClasses()} onClick={handleClick}>
        {renderIcon()}
        {renderActiveIndicator()}
        {renderCostBadge()}
        {renderPurchaseBadge()}
        {renderUnlockEffect()}
      </div>

      {/* Desktop tooltip on hover */}
      {!isMobile && !isBlocked && (
        <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-150 z-[100]">
          <div className="bg-slate-900/95 border border-slate-600/50 rounded-lg px-3 py-2 min-w-[180px] shadow-xl text-center">
            <div className="text-sm font-bold text-white mb-1">
              {upgrade.name}
              {purchaseCount > 0 && (
                <span className="text-slate-400 font-normal ml-1">
                  ({purchaseCount}/{upgrade.maxPurchases})
                </span>
              )}
            </div>
            <div className="text-xs text-slate-300 mb-2">{upgrade.description}</div>

            {/* Show maxed state */}
            {isMaxed && (
              <div className="text-xs text-amber-400 font-bold">Fully Upgraded!</div>
            )}

            {/* Show next level cost if not maxed */}
            {!isMaxed && (status === "available" || status === "unlocked") && (
              <div className="flex items-center justify-center gap-1 text-xs">
                <span className="text-slate-400">
                  {purchaseCount > 0 ? "Next:" : "Cost:"}
                </span>
                <img
                  src="/images/icon-coins.png"
                  alt=""
                  className="w-3 h-3"
                  style={{ imageRendering: "pixelated" }}
                />
                <span className={canAfford ? "text-amber-400" : "text-red-400"}>
                  {getUpgradeCost(upgrade, purchaseCount)} gold
                </span>
              </div>
            )}

            {upgrade.type === "active" && (
              <div className="text-[10px] text-purple-400 mt-1">Active Ability</div>
            )}
          </div>
          {/* Tooltip arrow */}
          <div className="absolute left-1/2 -translate-x-1/2 -bottom-1 w-2 h-2 bg-slate-900/95 border-r border-b border-slate-600/50 rotate-45" />
        </div>
      )}

    </div>
  )
}
